import React, { useState } from 'react';
import { ShieldAlert, Check } from 'lucide-react';

interface AgeGateProps {
  onConfirm: () => void;
}

/**
 * 18+ entry interstitial. Shown on first visit (per device) before the storefront.
 * Confirmation is remembered in localStorage by AppRoot. This is an age-affirmation
 * gate — government photo ID is verified at delivery/pickup, not here.
 */
const AgeGate: React.FC<AgeGateProps> = ({ onConfirm }) => {
  const [denied, setDenied] = useState(false);

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-forge-deep"
        style={{
          backgroundImage: 'url(/images/machete-wall.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <div className="absolute inset-0 bg-forge-deep/85 backdrop-blur-sm" />

      <div className="relative w-full max-w-lg bg-paper-bright shadow-float border border-ink-200 animate-in fade-in zoom-in-95 duration-300 ease-editorial">
        {/* Header */}
        <div className="bg-forge text-paper px-7 py-6">
          <div className="flex items-center gap-3 mb-2">
            <img src="/images/logo-shield.webp" alt="" className="w-14 h-14 object-contain" />
            <span className="font-editorial italic text-xl text-paper-warm tracking-tight leading-none">
              The Machete Man
            </span>
          </div>
          <p className="font-mono text-[10px] tracking-micro uppercase text-ember-soft">
            — Age verification
          </p>
        </div>

        {denied ? (
          <div className="p-7 lg:p-9 text-center">
            <div className="w-14 h-14 bg-ember/10 rounded-full flex items-center justify-center mx-auto mb-5">
              <ShieldAlert className="w-6 h-6 text-ember" strokeWidth={1.5} />
            </div>
            <h2 className="font-editorial italic text-2xl text-ink-950 mb-3 tracking-tight">
              Come back when you're 18.
            </h2>
            <p className="text-sm text-ink-700 leading-relaxed max-w-sm mx-auto">
              You must be at least 18 years old to browse or buy from The Machete Man. We sell
              machetes and bladed tools intended for lawful agricultural, outdoor, and utility use only.
            </p>
            <button
              onClick={() => setDenied(false)}
              className="mt-7 text-sm text-ink-700 hover:text-ember transition-colors duration-150"
            >
              ← Back
            </button>
          </div>
        ) : (
          <div className="p-7 lg:p-9">
            <h2 className="font-editorial italic font-light text-3xl text-ink-950 tracking-tight leading-tight mb-4">
              Are you 18 or older?
            </h2>
            <p className="text-sm text-ink-700 leading-relaxed mb-2">
              The Machete Man sells machetes and bladed tools for agricultural, outdoor, and utility
              use. You must be <span className="font-medium text-ink-950">18 years of age or older</span> to enter.
            </p>
            <p className="text-sm text-ink-700 leading-relaxed mb-7">
              Valid government-issued <span className="font-medium text-ink-950">photo ID is required on delivery</span>.
              By entering you agree to our Terms of Sale and confirm you will use this product lawfully.
            </p>

            <div className="space-y-3">
              <button
                onClick={onConfirm}
                className="w-full py-3.5 bg-ember hover:bg-ember-deep text-paper-warm text-sm font-medium transition-colors duration-150 ease-editorial inline-flex items-center justify-center gap-2"
              >
                <Check className="w-4 h-4" strokeWidth={1.5} />
                Yes, I am 18 or older
              </button>
              <button
                onClick={() => setDenied(true)}
                className="w-full py-3.5 border border-ink-200 text-ink-700 hover:border-ink-300 hover:bg-paper text-sm font-medium transition-colors duration-150 ease-editorial"
              >
                No, I am under 18
              </button>
            </div>

            <p className="mt-6 font-mono text-[10px] tracking-micro uppercase text-ink-500 text-center leading-relaxed">
              18+ only · Photo ID required on delivery
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgeGate;
